import { useState, useMemo } from 'react'
import { useWinLossAnalysis } from '@/hooks/use-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ReportFilters, getDateRange as getReportDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Download, Trophy, TrendingUp, TrendingDown, Target, Loader2 } from 'lucide-react'
import { formatCurrency, formatDate, toDisplayDate } from '@/lib/utils'
import {
  CHART_COLORS,
  DEFAULT_CHART_MARGIN,
  ROTATED_LABEL_CHART_MARGIN,
  formatChartCount,
  GRACKLE_COLORS,
  CATEGORICAL,
  SEMANTIC,
} from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'

const COLORS = CHART_COLORS

export default function WinLossAnalysis() {
  const [filters, setFilters] = useState<ReportFilterValues>(() =>
    getReportDateRange('last_12_months')
  )

  const { data, isLoading, error } = useWinLossAnalysis({
    startDate: filters.startDate,
    endDate: filters.endDate,
    ...(filters.owner_id ? { owner_id: filters.owner_id } : {}),
  })

  // Transform by-stage data for bar chart
  const byStageChartData = useMemo(() => {
    if (!data?.byStage) return []
    return data.byStage.map((item: any) => ({
      stage: item.stage_name,
      wonCount: item.won_count,
      lostCount: item.lost_count,
      winRate: parseFloat(item.win_rate || '0'),
    }))
  }, [data])

  // Transform trends data for line chart
  const trendsChartData = useMemo(() => {
    if (!data?.trends) return []
    return data.trends.map((item: any) => ({
      month: (toDisplayDate(item.month) ?? new Date(item.month)).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      }),
      winRate: parseFloat(item.win_rate || '0'),
      wonCount: item.won_count,
      lostCount: item.lost_count,
    }))
  }, [data])

  // Transform loss reasons data for pie chart
  const lossReasonsChartData = useMemo(() => {
    if (!data?.lossReasons) return []
    return data.lossReasons
      .filter((item: any) => item.loss_reason)
      .map((item: any) => ({
        name: item.loss_reason,
        value: item.count,
      }))
  }, [data])

  // Transform competitors data for bar chart
  const competitorsChartData = useMemo(() => {
    if (!data?.competitors) return []
    return data.competitors
      .filter((item: any) => item.competitor)
      .map((item: any) => ({
        competitor: item.competitor,
        losses: item.count,
      }))
  }, [data])

  const overall = data?.overall

  const handleExportCSV = () => {
    if (!data?.byStage) return

    const exportData = data.byStage.map((item: any) => ({
      Stage: item.stage_name,
      'Won Count': item.won_count,
      'Lost Count': item.lost_count,
      Total: item.total_count,
      'Win Rate': `${item.win_rate}%`,
      'Avg Won Amount (CAD)': item.avg_won_amount,
      'Avg Lost Amount (CAD)': item.avg_lost_amount,
    }))

    exportToCSV({ data: exportData, filename: 'win-loss-analysis.csv' })
  }

  // Check if we have data but it's empty
  const hasEmptyResults =
    !isLoading && !error && data && (!data.byStage || data.byStage.length === 0)

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Win/Loss Analysis</h1>
          <p className="text-muted-foreground">Analyze win rates and loss patterns</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-destructive font-medium">Failed to load win/loss analysis data.</p>
              <p className="text-sm text-muted-foreground">
                Error: {explainError(error, 'Unknown error occurred')}
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                aria-label="Reload page"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Win/Loss Analysis</h1>
          <p className="text-muted-foreground">Analyze win rates and loss patterns</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="last_12_months"
            datePresets={[
              'this_month',
              'last_month',
              'this_quarter',
              'fiscal_year',
              'calendar_year',
              'last_12_months',
              'all',
              'custom',
            ]}
          />
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={isLoading || !data?.byStage}
            aria-label="Export win/loss analysis to CSV"
            className="self-end"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Empty State Warning */}
      {hasEmptyResults && (
        <Card
          className="border"
          style={{ backgroundColor: SEMANTIC.warning.fill, borderColor: GRACKLE_COLORS.pollen }}
        >
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                No closed opportunities found in the selected date range
              </p>
              <p className="text-sm" style={{ color: SEMANTIC.warning.text }}>
                {filters.startDate && filters.endDate ? (
                  <>
                    Your filter from {formatDate(filters.startDate)} to{' '}
                    {formatDate(filters.endDate)} returned no results. Try adjusting your date range
                    or clearing the filters to see all data.
                  </>
                ) : (
                  'No closed opportunity data available. Try clearing the filters or selecting a different date range.'
                )}
              </p>
              {(filters.startDate || filters.endDate) && (
                <Button
                  onClick={() => setFilters(getReportDateRange('last_12_months'))}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  aria-label="Clear date range filters"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-label="Loading win/loss analysis data"
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Win Rate</CardTitle>
                <Trophy
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {overall?.win_rate?.toFixed(1) || '0.0'}%
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  overall percentage
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Total Won</CardTitle>
                <TrendingUp
                  className="h-4 w-4"
                  style={{ color: GRACKLE_COLORS.navy }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {overall?.won_count?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  opportunities won
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Total Lost</CardTitle>
                <TrendingDown
                  className="h-4 w-4"
                  style={{ color: GRACKLE_COLORS.darkRaspberry }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {overall?.lost_count?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  opportunities lost
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Avg Won Amount</CardTitle>
                <Target
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.steel.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(overall?.avg_won_deal_size || 0, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">per closed won</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Win/Loss Trends Line Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Win Rate Trends</CardTitle>
                <CardDescription>Monthly win rate percentage over time</CardDescription>
              </CardHeader>
              <CardContent>
                {trendsChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No trends data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendsChartData} margin={DEFAULT_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, name: string) => {
                          if (name === 'Win Rate') return `${value.toFixed(1)}%`
                          return value
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="winRate"
                        stroke={COLORS[2]}
                        strokeWidth={2}
                        name="Win Rate"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* By Stage Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Win/Loss by Stage</CardTitle>
                <CardDescription>Won vs lost opportunities by final stage</CardDescription>
              </CardHeader>
              <CardContent>
                {byStageChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No stage data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={byStageChartData} margin={ROTATED_LABEL_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="stage"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tickFormatter={formatChartCount} tick={{ fontSize: 11 }} />
                      <Tooltip content={<ChartTooltipContent />} />
                      <Legend />
                      <Bar dataKey="wonCount" fill={COLORS[0]} name="Won" />
                      <Bar dataKey="lostCount" fill={COLORS[3]} name="Lost" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Loss Reasons Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Top Loss Reasons</CardTitle>
                <CardDescription>Why opportunities were lost</CardDescription>
              </CardHeader>
              <CardContent>
                {lossReasonsChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No loss reason data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={lossReasonsChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => entry.name}
                        outerRadius={80}
                        fill={COLORS[0]}
                        dataKey="value"
                      >
                        {lossReasonsChartData.map((_entry: unknown, index: number) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltipContent />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Competitors Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Losses by Competitor</CardTitle>
                <CardDescription>Which competitors are winning deals</CardDescription>
              </CardHeader>
              <CardContent>
                {competitorsChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No competitor data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={competitorsChartData} margin={ROTATED_LABEL_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="competitor"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tickFormatter={formatChartCount} tick={{ fontSize: 11 }} />
                      <Tooltip content={<ChartTooltipContent />} />
                      <Legend />
                      <Bar dataKey="losses" fill={COLORS[4]} name="Lost Deals" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Win/Loss Metrics by Stage</CardTitle>
              <CardDescription>Complete breakdown of win and loss metrics</CardDescription>
            </CardHeader>
            <CardContent>
              {!data?.byStage || data.byStage.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  No detailed metrics available
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm"
                    role="table"
                    aria-label="Win/loss detailed metrics"
                  >
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Stage</th>
                        <th className="text-right p-2 font-medium">Won</th>
                        <th className="text-right p-2 font-medium">Lost</th>
                        <th className="text-right p-2 font-medium">Total</th>
                        <th className="text-right p-2 font-medium">Win Rate</th>
                        <th className="text-right p-2 font-medium">Avg Won $</th>
                        <th className="text-right p-2 font-medium">Avg Lost $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byStage.map((item: any, index: number) => (
                        <tr key={index} className="border-b hover:bg-muted/50">
                          <td className="p-2">{item.stage_name}</td>
                          <td className="text-right p-2">{item.won_count}</td>
                          <td className="text-right p-2">{item.lost_count}</td>
                          <td className="text-right p-2">{item.total_count}</td>
                          <td className="text-right p-2">
                            {parseFloat(item.win_rate || '0').toFixed(1)}%
                          </td>
                          <td className="text-right p-2">
                            {formatCurrency(parseFloat(item.avg_won_amount || '0'), 'CAD')}
                          </td>
                          <td className="text-right p-2">
                            {formatCurrency(parseFloat(item.avg_lost_amount || '0'), 'CAD')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
